import { onUnmounted, readonly, ref } from 'vue'
import {
  START_LOCATION,
  useRouter,
  type NavigationGuardReturn,
  type RouteLocationNormalized,
} from 'vue-router'
import { applyAppUpdate, fetchDeployedVersion } from '@/composables/appUpdate'

const POLL_INTERVAL_MS = 5 * 60 * 1000

/**
 * Detects that this tab runs an older build than the deployed one and moves it
 * onto the new build.
 *
 * `updateAvailable` means `/version.json` differs from the `__APP_VERSION__`
 * compiled into this bundle (checked at start, every `POLL_INTERVAL_MS` and
 * after each navigation). While an update is available, the next in-app
 * navigation is cancelled and replaced by a full load of its destination —
 * served from the network, so it runs the new build; the user is leaving the
 * page anyway, so no input is lost. `UpdateBanner` covers users who stay on
 * one page.
 */
export function useVersionCheck() {
  const router = useRouter()
  const updateAvailable = ref(false)
  let intervalId: ReturnType<typeof setInterval> | null = null
  let removeGuards: Array<() => void> = []

  async function check() {
    const deployed = await fetchDeployedVersion()
    if (deployed === null || deployed === __APP_VERSION__) return
    updateAvailable.value = true
  }

  function updateInsteadOfNavigating(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
  ): NavigationGuardReturn {
    if (!updateAvailable.value || from === START_LOCATION) return true
    void applyAppUpdate(router.resolve(to).href)
    return false
  }

  function startPolling() {
    check()
    intervalId = setInterval(check, POLL_INTERVAL_MS)
    removeGuards = [router.beforeEach(updateInsteadOfNavigating), router.afterEach(() => check())]
  }

  function stopPolling() {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
    removeGuards.forEach((remove) => remove())
    removeGuards = []
  }

  onUnmounted(() => {
    stopPolling()
  })
  return { updateAvailable: readonly(updateAvailable), startPolling, stopPolling }
}
